/**
 * UrgentAlertModal — the "you cannot miss this" pop-up.
 *
 * Why (Andy, 2026-10-12, after Colin Espie's risky sign-in): "We need proper
 * alerting popups … we nearly missed it." A toast slides away after a few
 * seconds and a badge is easy to walk past, so urgent kinds — P1/P2 tickets,
 * risky users, security incidents/alerts, network outages — open this instead:
 * centred, above everything, with a sound and a flashing tab title, and it
 * stays until somebody acknowledges it.
 *
 * Acknowledge marks them read (the record stays in the Notification Centre).
 * "Later" only closes it — the items stay unread and the bell stays solid.
 *
 * The list of what counts as urgent is URGENT_NOTIFICATION_KINDS in the shared
 * package, the same list the API uses to decide what to push to the desktop.
 */

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

const KIND_META = {
  'servicedesk-priority': { icon: 'fa-ticket', label: 'ServiceDesk', urlLabel: 'Open ticket' },
  'risky-user': { icon: 'fa-user-secret', label: 'Risky user', urlLabel: 'Open in Entra' },
  'security-incident': { icon: 'fa-shield-halved', label: 'Security incident', urlLabel: 'Open' },
  'security-alert': { icon: 'fa-triangle-exclamation', label: 'Security alert', urlLabel: 'Open' },
  'network-incident': { icon: 'fa-network-wired', label: 'Network', urlLabel: 'Open' }
};

const TAB_LABEL = {
  'security-dashboard': 'Security Hub',
  'network-dashboard': 'Network',
  dashboard: 'Dashboard'
};

function timeOf(iso) {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

/**
 * One line saying where a critical alert stands: who has it, or how long until
 * everyone is told. Shared with the notification panel so both say the same.
 */
export function alertStatusText(alert, nowMs = Date.now()) {
  if (!alert) return null;
  if (alert.stage === 'claimed') return { tone: 'ok', text: `${alert.claimedBy?.name ?? 'Someone'} has this` };
  if (alert.stage === 'resolved') return { tone: 'ok', text: `Resolved${alert.resolvedReason ? `: ${alert.resolvedReason}` : ''}` };
  if (alert.stage === 'expired') return { tone: 'muted', text: 'Nobody claimed it; no longer being chased' };
  if (alert.stage === 'on-call') {
    const mins = alert.escalateAt ? Math.max(0, Math.ceil((Date.parse(alert.escalateAt) - nowMs) / 60000)) : null;
    return { tone: 'bad', text: mins === null ? 'Sent to on call' : `Sent to on call — everyone is told in ${mins} min if nobody claims it` };
  }
  return { tone: 'bad', text: 'Sent to everyone — nobody has claimed it yet' };
}

// Readable on both grounds (the Defender widget idiom): the plain red sinks on a dark card.
export const TONE_COLOUR = {
  ok: 'color-mix(in srgb, var(--ok) 62%, var(--ink))',
  bad: 'color-mix(in srgb, var(--bad) 62%, var(--ink))',
  muted: 'var(--muted)'
};

/** The default: a short, attention-getting two-tone chime. Silent if the browser blocks audio. */
export function playAlertChime() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const notes = [880, 660, 880, 660];
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      const t = ctx.currentTime + i * 0.22;
      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.exponentialRampToValueAtTime(0.25, t + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.2);
      osc.connect(gain).connect(ctx.destination);
      osc.start(t);
      osc.stop(t + 0.21);
    });
    setTimeout(() => void ctx.close().catch(() => undefined), 1400);
  } catch {
    // Autoplay policy or no audio device — the pop-up itself still shows.
  }
}

/**
 * Red alert (only when an admin switches it on in Admin Settings → System →
 * Urgent Alert Sound): three rising klaxon "whoops" (the starship kind). Two slightly
 * detuned sawtooth oscillators sweep 280 -> 760 Hz through a low-pass filter,
 * which gives the horn its body without being shrill. The phone app plays the
 * same shape from a rendered file (apps/field-android/tools/gen-red-alert.mjs).
 * Silent if the browser blocks audio.
 */
export function playRedAlert() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 2400;
    filter.Q.value = 2;
    const master = ctx.createGain();
    master.gain.value = 0.16;
    filter.connect(master).connect(ctx.destination);
    const WHOOP = 0.62;
    const GAP = 0.28;
    for (let i = 0; i < 3; i++) {
      const t = ctx.currentTime + 0.05 + i * (WHOOP + GAP);
      const env = ctx.createGain();
      env.gain.setValueAtTime(0.0001, t);
      env.gain.exponentialRampToValueAtTime(1, t + 0.025);
      env.gain.setValueAtTime(1, t + WHOOP - 0.06);
      env.gain.exponentialRampToValueAtTime(0.0001, t + WHOOP);
      env.connect(filter);
      for (const detune of [1, 1.012]) {
        const osc = ctx.createOscillator();
        osc.type = 'sawtooth';
        osc.frequency.setValueAtTime(280 * detune, t);
        osc.frequency.exponentialRampToValueAtTime(760 * detune, t + WHOOP);
        osc.connect(env);
        osc.start(t);
        osc.stop(t + WHOOP + 0.02);
      }
    }
    setTimeout(() => void ctx.close().catch(() => undefined), 3500);
  } catch {
    // Autoplay policy or no audio device — the pop-up itself still shows.
  }
}

// While nobody has answered, sound the klaxon again this often.
const REPEAT_MS = 45_000;

export default function UrgentAlertModal({ items, klaxon = false, onAcknowledge, onLater, onOpenTab, pushState, onEnablePush, alertsById = {}, onClaim, claimingId, onIgnore, ignoreBlockedReason }) {
  const open = items.length > 0;
  const [silenced, setSilenced] = useState(false);

  // Each new pop-up starts loud again.
  useEffect(() => {
    if (!open) setSilenced(false);
  }, [open]);

  // With the klaxon switched on, keep sounding red alert until somebody
  // acknowledges, claims or silences it. The plain chime plays once.
  useEffect(() => {
    if (!open || silenced || !klaxon) return undefined;
    const timer = setInterval(playRedAlert, REPEAT_MS);
    return () => clearInterval(timer);
  }, [open, silenced, klaxon]);

  // Flash the tab title until acknowledged, so a backgrounded tab is noticed.
  useEffect(() => {
    if (!open) return undefined;
    // If the effect re-runs mid-flash (another alert arrived), the current
    // title may be the flashed one — never capture that as the original.
    const original = /Urgent alert$/.test(document.title) ? (window.__omniBaseTitle || 'Omni Cloud') : document.title;
    window.__omniBaseTitle = original;
    let on = false;
    const timer = setInterval(() => {
      on = !on;
      document.title = on ? `(${items.length}) ⚠ Urgent alert` : original;
    }, 1000);
    return () => {
      clearInterval(timer);
      document.title = original;
    };
  }, [open, items.length]);

  useEffect(() => {
    if (!open) return undefined;
    function onKey(e) {
      if (e.key === 'Escape') onLater();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onLater]);

  if (!open) return null;

  return createPortal(
    <div className="urgent-backdrop urgent-red-alert" role="presentation">
      <div className="urgent-modal" role="alertdialog" aria-modal="true" aria-labelledby="urgent-title">
        <div className="urgent-head">
          <span className="urgent-head-icon"><i className="fas fa-bell" aria-hidden="true" /></span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div id="urgent-title" className="urgent-head-title">
              {items.length === 1 ? 'Urgent alert' : `${items.length} urgent alerts`}
            </div>
            <div className="urgent-head-sub">Needs somebody to look now</div>
          </div>
          {klaxon && (<button
            type="button"
            className="urgent-silence-btn"
            onClick={() => setSilenced((v) => !v)}
            aria-pressed={silenced}
            title={silenced ? 'The alarm is silenced. Click to let it sound again.' : 'Stop the alarm repeating (the alert stays open)'}
          >
            <i className={`fas ${silenced ? 'fa-volume-xmark' : 'fa-volume-high'}`} aria-hidden="true" />
            <span>{silenced ? 'Silenced' : 'Silence'}</span>
          </button>)}
        </div>

        <div className="urgent-body">
          {items.map((item) => {
            const meta = KIND_META[item.kind] ?? { icon: 'fa-circle-exclamation', label: 'Alert', urlLabel: 'Open' };
            const url = item.link?.url && /^https:\/\//i.test(item.link.url) ? item.link.url : null;
            const tab = item.link?.tab;
            const alertId = item.link?.alertId;
            const alert = alertId ? alertsById[alertId] : null;
            // A P1 shows its alert's state; a P2 (no alert record) shows who
            // picked the ticket up in ServiceDesk once somebody has.
            const status = alertStatusText(alert)
              ?? (item.link?.pickedUpBy?.name ? { tone: 'ok', text: `Picked up by ${item.link.pickedUpBy.name}` } : null);
            const claimable = Boolean(alertId && onClaim && (!alert || alert.stage === 'on-call' || alert.stage === 'everyone'));
            return (
              <div key={item.id} className="urgent-item">
                <span className="urgent-item-icon"><i className={`fas ${meta.icon}`} aria-hidden="true" /></span>
                <div className="urgent-item-text">
                  <div className="urgent-item-meta">{meta.label} · {timeOf(item.createdAt)}</div>
                  <div className="urgent-item-title">{item.title}</div>
                  <div className="urgent-item-body">{item.body}</div>
                  {status && (
                    <div style={{ marginTop: 4, fontSize: 12, fontWeight: 700, color: TONE_COLOUR[status.tone] }}>{status.text}</div>
                  )}
                  <div className="urgent-item-actions">
                    {claimable && (
                      <button
                        type="button"
                        className="lg-btn urgent-ack-btn urgent-small-btn"
                        disabled={claimingId === alertId}
                        onClick={() => onClaim(item)}
                        title="Tell everyone you are dealing with this — it stops the escalation"
                      >
                        {claimingId === alertId
                          ? <i className="fas fa-spinner fa-spin" aria-hidden="true" />
                          : <><i className="fas fa-hand" aria-hidden="true" /> I&apos;ve got it</>}
                      </button>
                    )}
                    {url && (
                      <a className="lg-btn lg-btn--glass urgent-small-btn" href={url} target="_blank" rel="noopener noreferrer">
                        <i className="fas fa-arrow-up-right-from-square" aria-hidden="true" /> {meta.urlLabel}
                      </a>
                    )}
                    {tab && TAB_LABEL[tab] && (
                      <button type="button" className="lg-btn lg-btn--glass urgent-small-btn" onClick={() => onOpenTab(item)}>
                        Go to {TAB_LABEL[tab]}
                      </button>
                    )}
                    {onIgnore && (() => {
                      const blocked = ignoreBlockedReason?.(item) ?? null;
                      return (
                        <button
                          type="button"
                          className="lg-btn lg-btn--glass urgent-small-btn"
                          onClick={() => onIgnore(item)}
                          disabled={Boolean(blocked)}
                          title={blocked ?? 'Remove it from your list — nobody else is affected'}
                        >
                          <i className="fas fa-xmark" aria-hidden="true" /> Ignore
                        </button>
                      );
                    })()}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {pushState === 'off' && (
          <div className="urgent-push-hint">
            <i className="fas fa-desktop" aria-hidden="true" />
            <span>Get these even when Omni is closed.</span>
            <button type="button" className="lg-btn lg-btn--glass urgent-small-btn" onClick={onEnablePush}>
              Turn on desktop alerts
            </button>
          </div>
        )}

        <div className="urgent-foot">
          <button type="button" className="lg-btn lg-btn--glass" onClick={onLater} title="Close — they stay unread in Notifications">
            Later
          </button>
          <button type="button" className="lg-btn urgent-ack-btn" onClick={onAcknowledge} autoFocus>
            <i className="fas fa-check" aria-hidden="true" /> Acknowledge
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
